import { useQuery, useMutation } from '@apollo/client';
import { useNotifications } from '@sixfold/common-ui';
import React from 'react';
import { useParams } from 'react-router';

import {
  CompanyWithFeatureFlagMetadataQuery,
  CompanyWithFeatureFlagMetadataQueryVariables,
  PAGINATION_PAGE_SIZE,
  UpdateCompanyFeatureFlagMutation,
  UpdateCompanyFeatureFlagMutationVariables,
  loadMoreFromConnection,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { CompanyFeatureFlags } from '../components/company_feature_flags';
import { companyFeatureFlagsMetadataQuery, updateCompanyFeatureFlagMutation } from '../graphql';

interface CompanyFeatureFlagsViewDataProps {
  data: {
    company?: CompanyWithFeatureFlagMetadataQuery['company'] | null;
    featureFlags?: CompanyWithFeatureFlagMetadataQuery['featureFlagMetadata'] | null;
  };
}

class LoadingContainer extends Loader<
  CompanyWithFeatureFlagMetadataQuery,
  CompanyFeatureFlagsViewDataProps,
  CompanyWithFeatureFlagMetadataQueryVariables
> {}

export const CompanyFeatureFlagsContainer: React.FC<{ onlyFlagIds?: readonly string[] }> = ({ onlyFlagIds }) => {
  const { company_id } = useParams() as { company_id: string };
  const notify = useNotifications();
  const result = useQuery<CompanyWithFeatureFlagMetadataQuery, CompanyWithFeatureFlagMetadataQueryVariables>(
    companyFeatureFlagsMetadataQuery,
    {
      variables: {
        company_id,
        applies_to: 'company',
        first: PAGINATION_PAGE_SIZE,
      },
    },
  );
  const [updateCompanyFeatureFlag] = useMutation<
    UpdateCompanyFeatureFlagMutation,
    UpdateCompanyFeatureFlagMutationVariables
  >(updateCompanyFeatureFlagMutation);

  return (
    <LoadingContainer
      result={result}
      mapData={({ data }) => ({
        data: {
          company: data.company,
          featureFlags: data.featureFlagMetadata,
        },
      })}>
      {({ data }) => {
        const LoadMoreData = () => {
          return loadMoreFromConnection(companyFeatureFlagsMetadataQuery, result, [
            'company',
            'featureFlagAuditLogConnection',
          ]);
        };
        return (
          <CompanyFeatureFlags
            loadMoreAuditLogs={LoadMoreData}
            allFlags={data.featureFlags}
            onlyFlagIds={onlyFlagIds}
            currentSetFlags={(data.company?.feature_flags as Record<string, boolean>) ?? {}}
            company={data.company}
            onFlagsChange={async (input) => {
              const response = await updateCompanyFeatureFlag({
                ...input,
                refetchQueries: [
                  {
                    query: companyFeatureFlagsMetadataQuery,
                    variables: {
                      company_id,
                      applies_to: 'company',
                      first: PAGINATION_PAGE_SIZE,
                    },
                  },
                ],
              });
              notify.success({ title: 'Feature flag updated' });
              return response;
            }}
          />
        );
      }}
    </LoadingContainer>
  );
};
